/**
 * Business Review - /review (replaces the AI Intelligence page; /ai redirects here).
 *
 * The week the way a category team reads it: outputs (revenue, units) explained by inputs (price, availability,
 * selection, ad spend, returns), compared week-on-week, against the 4 weeks before and year-on-year.
 * URL state: ?week=YYYY-MM-DD (any day of the Monday–Sunday week; default the last complete week),
 * ?platform= ?brand= ?category= filters. Tabs: Weekly review, Availability, Profit & ads (admins), Selection.
 * The Ask box at the bottom is grounded on the same numbers (components/review/AskPanel).
 * Data basis (stock freshness, YoY availability, which platforms send order ids) is stated on the page from
 * GET /api/review/coverage and the tabs' own notes.
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { isAdmin } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LoadError } from '../components/LoadError';
import { useSalesOptions } from '../components/sales/views/common';
import { platformLabel } from '../config/platforms';
import { shiftDay } from '../components/sales/format';
import { isCompleteWeek, lastCompleteWeek, shiftWeek, useReviewData, weekLabel, weekStart } from '../components/review/reviewApi';
import { WeeklyReviewTab } from '../components/review/WeeklyReviewTab';
import { AvailabilityTab } from '../components/review/AvailabilityTab';
import { ProfitTab } from '../components/review/ProfitTab';
import { SelectionTab } from '../components/review/SelectionTab';
import { AskPanel } from '../components/review/AskPanel';

import { useChannels } from '../hooks/useChannels';
const TABS = [['weekly', 'Weekly review'], ['availability', 'Availability'], ['profit', 'Profit & ads'], ['selection', 'Selection']];
const isDay = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));

const Select = ({ id, label, value, onChange, options, allLabel }) => (
  <label className="flex items-center gap-1.5 text-xs text-gray-600" htmlFor={id}>
    <span className="sr-only">{label}</span>
    <select id={id} value={value || 'ALL'} onChange={e => onChange(e.target.value)} data-testid={id}
      className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      <option value="ALL">{allLabel}</option>
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  </label>
);

export default function BusinessReviewPage() {
  const channelMaster = useChannels();   // also re-renders when channel names or colours change
  const { user } = useAuth();
  const showCosts = isAdmin(user);
  const { country } = useCountry();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([key]) => key === params.get('tab')) && !(params.get('tab') === 'profit' && !showCosts) ? params.get('tab') : 'weekly';
  const week = useMemo(() => (isDay(params.get('week')) ? weekStart(params.get('week')) : lastCompleteWeek()), [params]);
  const platform = params.get('platform') || '';
  const brand = params.get('brand') || '';
  const category = params.get('category') || '';
  const options = useSalesOptions(country, true);

  const patch = changes => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => { if (value == null || value === '' || value === 'ALL') next.delete(key); else next.set(key, String(value)); });
    setParams(next, { replace: true });
  };

  const scope = { country, week, platform, brand, category };
  const coverage = useReviewData('coverage', { country });
  const wbr = useReviewData(tab === 'weekly' ? 'wbr' : null, scope);
  const availability = useReviewData(tab === 'availability' ? 'availability' : null, { country, week, platform });
  const profit = useReviewData(tab === 'profit' && showCosts ? 'profit' : null, scope);
  const selection = useReviewData(tab === 'selection' ? 'selection' : null, { country, week, platform });
  const current = { weekly: wbr, availability, profit, selection }[tab];
  const nextDisabled = !isCompleteWeek(shiftWeek(week, 1));

  const basis = useMemo(() => {
    const c = coverage.data;
    if (!c) return [];
    const notes = [];
    const platforms = Object.values(c.platforms || {});
    if (platforms.length) {
      const first = platforms.map(p => p.first).sort()[0];
      notes.push(`Sales data from ${first} on ${platforms.length} platform${platforms.length === 1 ? '' : 's'}`);
      const withOrders = platforms.filter(p => p.has_orders).map(p => p.label);
      notes.push(withOrders.length ? `order counts for ${withOrders.join(', ')} only` : 'no platform sends order ids yet');
    }
    notes.push(c.snapshot_start ? `daily stock history since ${c.snapshot_start}` : 'daily stock history starts with the next daily job');
    notes.push(c.has_ad_spend ? 'ad spend recorded' : 'no ad spend uploaded');
    return notes;
  }, [coverage.data]);

  const filters = [platform && platformLabel(platform), brand, category].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="business-review-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" data-testid="review-heading">Business Review</h1>
          <p className="mt-1 text-sm text-gray-500">Week of {weekLabel(week)} · compared with the week before, the 4-week average and last year</p>
          {basis.length > 0 && <p className="mt-0.5 text-xs text-gray-500" data-testid="review-basis">{basis.join(' · ')}.</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => current?.refresh()} disabled={current?.loading} aria-label="Refresh" data-testid="review-refresh">
          <RefreshCw className={`h-4 w-4 ${current?.loading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </Button>
      </header>

      <section className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2" aria-label="Week and filters" data-testid="review-controls">
        <div className="inline-flex items-center rounded-lg bg-gray-100 p-0.5">
          <button type="button" onClick={() => patch({ week: shiftWeek(week, -1) })} aria-label="Previous week" className="h-7 w-7 rounded-md text-gray-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="review-prev-week"><ChevronLeft className="mx-auto h-4 w-4" aria-hidden="true" /></button>
          <input type="date" value={week} max={shiftDay(lastCompleteWeek(), 6)} onChange={e => { if (isDay(e.target.value)) patch({ week: e.target.value }); }} aria-label="Week"
            className="h-7 bg-transparent px-1 text-xs tabular-nums text-gray-900 focus:outline-none" data-testid="review-week-input" />
          <button type="button" onClick={() => patch({ week: shiftWeek(week, 1) })} disabled={nextDisabled} aria-label="Next week" className="h-7 w-7 rounded-md text-gray-700 hover:bg-white disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="review-next-week"><ChevronRight className="mx-auto h-4 w-4" aria-hidden="true" /></button>
        </div>
        {week !== lastCompleteWeek() && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => patch({ week: '' })}>Last complete week</Button>}
        <span className="mx-1 hidden h-5 w-px bg-gray-200 sm:block" aria-hidden="true" />
        <Select id="review-platform" label="Platform" value={platform} onChange={value => patch({ platform: value })} allLabel="All platforms"
          options={channelMaster.filters.map(c => ({ value: c.code, label: c.name }))} />
        <Select id="review-brand" label="Brand" value={brand} onChange={value => patch({ brand: value })} allLabel="All brands" options={options.brands.map(name => ({ value: name, label: name }))} />
        <Select id="review-category" label="Category" value={category} onChange={value => patch({ category: value })} allLabel="All categories" options={options.categories.map(name => ({ value: name, label: name }))} />
        {filters.length > 0 && (
          <button type="button" onClick={() => patch({ platform: '', brand: '', category: '' })} className="text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid="review-clear-filters">
            Clear {filters.join(' · ')}
          </button>
        )}
        {(tab === 'availability' || tab === 'selection') && (brand || category) && (
          <span className="text-xs text-gray-500">Availability and selection are by platform only; the brand / category filter applies to the other tabs.</span>
        )}
      </section>

      <Tabs value={tab} onValueChange={value => patch({ tab: value === 'weekly' ? '' : value })}>
        <TabsList aria-label="Review sections">
          {TABS.filter(([key]) => key !== 'profit' || showCosts).map(([key, label]) => (
            <TabsTrigger key={key} value={key} data-testid={`review-tab-${key}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        {current?.error && !current.data && (
          <div className="mt-4"><LoadError what="the business review" error={current.error} onRetry={current.refresh} retrying={current.loading} testId="review-load-error" /></div>
        )}
        {current?.error && current.data && <div className="mt-4"><LoadError what="the latest review" error={current.error} onRetry={current.refresh} retrying={current.loading} /></div>}
        {!current?.data && !current?.error && (
          <div className="mt-4 space-y-3" aria-busy="true" aria-label="Loading the review" data-testid="review-loading">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>
            <Skeleton className="h-64" />
          </div>
        )}
        <div className={`mt-4 transition-opacity ${current?.loading && current?.data ? 'opacity-60' : ''}`} aria-busy={current?.loading}>
          <TabsContent value="weekly"><WeeklyReviewTab data={wbr.data} showCosts={showCosts} onScope={patch} /></TabsContent>
          <TabsContent value="availability"><AvailabilityTab data={availability.data} showCosts={showCosts} /></TabsContent>
          {showCosts && <TabsContent value="profit"><ProfitTab data={profit.data} /></TabsContent>}
          <TabsContent value="selection"><SelectionTab data={selection.data} /></TabsContent>
        </div>
      </Tabs>

      <AskPanel country={country} week={week} platform={platform || null} brand={brand || null} category={category || null} />
    </div>
  );
}
